use '@use-gpu/wgsl/codec/octahedral'::{ decodeOctahedral, decodeHemiOctahedral, wrapOctahedral };
use '@use-gpu/wgsl/codec/cubemap'::{ encodeCubeMap };

@link var depthTexture: texture_depth_2d_array;
@link var depthSampler: sampler;

@link fn getTextureSize() -> vec2<f32>;
@link fn getShadowDepth() -> vec2<f32>;

// +Y is the only full face, -Y is unused, others are half and split between top and bottom half
const toLayer: array<u32, 6> = array(1,1,0,3,2,2);

@export fn getDepthQuadsToHemiSample(uv: vec2<f32>) -> f32 {
  let hemi = uv.xy * 2.0 - 1.0;

  // Remap octahedral Z to cubemap Y
  let uvw = decodeHemiOctahedral(hemi).yzx;
  if (uvw.y < 0.0) { return 1.0; }

  let xyl = encodeCubeMap(uvw);
  let hl = toLayer[xyl.layer];
  let hs = select((xyl.layer & 1u) + 1u, 0u, xyl.layer == 2u);

  // Remap to 1px enlarged projection
  let size = getTextureSize();
  let size2 = size.y / 2.0;
  let fovScale = (size - 1.0) / size;
  let fovScale2 = (size2 - 1.0) / size2;

  let uvf = vec2<f32>(
    (xyl.xy.x * fovScale.x) * .5 + .5,
    select(
      (xyl.xy.y * fovScale.y) * .5 + .5,
      ((xyl.xy.y + .5) * fovScale2) * .5 + .25,
      hs > 0u,
    ),
  );

  // Map +X/-X and +Z/-Z half-faces
  let hu = uvf.x;
  let cv = uvf.y;
  let hv = select(
    cv,
    select(cv, cv + .5, hs > 1u),
    hs > 0u
  );

  let huv = vec2<f32>(hu, hv);

  // Convert to source texels
  let xyf = huv * size - .5;
  let xyi = floor(xyf);
  let xyd = xyf - xyi;

  // Map to center of texel quads to avoid textureGather differences
  let xyc = xyi + 1.0;
  let uvc = xyc / size;

  // Bilinear
  let d4 = textureGather(depthTexture, depthSampler, uvc, hl);

  let sample = mix(
    mix(d4.w, d4.z, xyd.x),
    mix(d4.x, d4.y, xyd.x),
    xyd.y,
  );

  // Remap to radial depth
  let sd = getShadowDepth();
  let maxC = max(abs(uvw.x), max(abs(uvw.y), abs(uvw.z)));
  let depth = mix(sd.x, sample, maxC);

  return depth;
};
